import { useRef } from "react";
import { Camera, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export default function EditTeamDialog({ open, onOpenChange, edit, setEdit, seasons, onUpload, onSave }) {
  const logoInputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifica squadra</DialogTitle>
          <DialogDescription className="sr-only">
            Aggiorna i dati della squadra e carica il logo della società.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-2">
            <div
              className="h-24 w-24 rounded-2xl bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-slate-200"
              data-testid="team-logo-preview"
            >
              {edit.logo_url ? (
                <img src={edit.logo_url} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={32} className="text-slate-300" />
              )}
            </div>
            <div className="flex gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  onUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                data-testid="upload-logo-btn"
              >
                <Camera size={14} className="mr-1" />
                {edit.logo_url ? "Cambia logo" : "Carica logo società"}
              </Button>
              {edit.logo_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEdit({ ...edit, logo_url: null })}
                >
                  Rimuovi
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">JPG/PNG/WEBP · max 2MB</p>
          </div>
          <div>
            <Label>Nome</Label>
            <Input
              value={edit.name}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              data-testid="edit-team-name"
            />
          </div>
          <div>
            <Label>Categoria</Label>
            <Input
              value={edit.category}
              onChange={(e) => setEdit({ ...edit, category: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Stagione</Label>
              <Select
                value={edit.season || "__none__"}
                onValueChange={(v) => setEdit({ ...edit, season: v === "__none__" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="n/d" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— n/d —</SelectItem>
                  {seasons.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Colore</Label>
              <Input
                type="color"
                value={edit.color || "#0ea5e9"}
                onChange={(e) => setEdit({ ...edit, color: e.target.value })}
                className="h-10 p-1"
              />
            </div>
          </div>

          <details className="pt-2">
            <summary className="cursor-pointer text-sm font-semibold text-slate-700 hover:text-sky-600">
              Dati legali / fiscali (per modulistica)
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <Label>Denominazione legale (ASD/SSD)</Label>
                <Input
                  value={edit.legal_name || ""}
                  onChange={(e) => setEdit({ ...edit, legal_name: e.target.value })}
                  placeholder="A.S.D. Calcio Esempio"
                  data-testid="edit-team-legal-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input
                    value={edit.fiscal_code || ""}
                    onChange={(e) => setEdit({ ...edit, fiscal_code: e.target.value.toUpperCase() })}
                  />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input
                    value={edit.vat_number || ""}
                    onChange={(e) => setEdit({ ...edit, vat_number: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Matricola FIGC</Label>
                <Input
                  value={edit.figc_affiliation_code || ""}
                  onChange={(e) => setEdit({ ...edit, figc_affiliation_code: e.target.value })}
                />
              </div>
              <div>
                <Label>Sede legale</Label>
                <Input
                  value={edit.address || ""}
                  onChange={(e) => setEdit({ ...edit, address: e.target.value })}
                  placeholder="Via, civico, CAP, Comune"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefono</Label>
                  <Input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Nome Presidente</Label>
                <Input
                  value={edit.president_name || ""}
                  onChange={(e) => setEdit({ ...edit, president_name: e.target.value })}
                  placeholder="Nome e cognome del legale rappresentante"
                />
              </div>
            </div>
          </details>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={onSave}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="edit-team-save"
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
